import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    createCarrierZipPrefix,
    getCarrierZipPrefixById,
    updateCarrierZipPrefix,
    getCarriers,
    logError
} from '../api';
import { Button, Form, Alert } from 'react-bootstrap';
import EditCancelModal from './Global/EditCancelModal';

const CarrierZipPrefixForm = () => {
    const [carrierZipPrefix, setCarrierZipPrefix] = useState({
        carrierId: '',
        zipPrefix: '',
        priority: 100,
        notes: '',
        disabled: false
    });
    const [carriers, setCarriers] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [isDirty, setIsDirty] = useState(false);
    const [showCancelModal, setShowCancelModal] = useState(false);
    const [error, setError] = useState('');

    const { id } = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        loadCarriers();
        if (id) {
            setIsEditing(true);
            getCarrierZipPrefixById(id)
                .then(data => setCarrierZipPrefix(data))
                .catch(logError);
        }
    }, [id]);

    const loadCarriers = async () => {
        try {
            const data = await getCarriers();
            setCarriers(data.filter(c => !c.disabled));
        } catch (error) {
            logError(error);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setCarrierZipPrefix(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        setIsDirty(true);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setError('');
        
        const dataToSubmit = {
            ...carrierZipPrefix,
            carrierId: parseInt(carrierZipPrefix.carrierId),
            priority: parseInt(carrierZipPrefix.priority),
            disabled: carrierZipPrefix.disabled || false
        };

        if (isEditing) {
            updateCarrierZipPrefix(id, dataToSubmit)
                .then(() => navigate('/carrier-zip-prefixes'))
                .catch(err => {
                    if (err.response?.status === 409) {
                        setError('Ya existe un registro con el mismo Transportista y Prefijo de Código Postal');
                    } else {
                        logError(err);
                    }
                });
        } else {
            createCarrierZipPrefix(dataToSubmit)
                .then(() => navigate('/carrier-zip-prefixes'))
                .catch(err => {
                    if (err.response?.status === 409) {
                        setError('Ya existe un registro con el mismo Transportista y Prefijo de Código Postal');
                    } else {
                        logError(err);
                    }
                });
        }
    };

    const handleCancel = () => {
        if (isDirty) setShowCancelModal(true);
        else navigate('/carrier-zip-prefixes');
    };

    return (
        <div>
            <h2>{isEditing ? 'Editar Prefijo' : 'Agregar Nuevo Prefijo'}</h2>

            {error && (
                <Alert variant="danger" className="mb-3">
                    {error}
                </Alert>
            )}

            <Form onSubmit={handleSubmit}>
                <Form.Group>
                    <Form.Label>Transportista</Form.Label>
                    <Form.Control 
                        as="select" 
                        name="carrierId" 
                        value={carrierZipPrefix.carrierId} 
                        onChange={handleChange} 
                        required
                        data-testid="zip-prefix-carrier-select"
                    >
                        <option value="">Seleccionar Transportista</option>
                        {carriers.map(carrier => (
                            <option key={carrier.id} value={carrier.id}>
                                {carrier.firstName} {carrier.lastName1}
                            </option>
                        ))}
                    </Form.Control>
                </Form.Group>

                <Form.Group>
                    <Form.Label>Prefijo de Código Postal</Form.Label>
                    <Form.Control 
                        type="text" 
                        name="zipPrefix" 
                        value={carrierZipPrefix.zipPrefix} 
                        onChange={handleChange} 
                        required 
                        data-testid="zip-prefix-input"
                    />
                </Form.Group>

                <Form.Group>
                    <Form.Label>Prioridad</Form.Label>
                    <Form.Control 
                        type="number" 
                        name="priority" 
                        value={carrierZipPrefix.priority} 
                        onChange={handleChange} 
                        min="1"
                        max="999"
                        data-testid="zip-prefix-priority-input"
                    />
                    <Form.Text className="text-muted">
                        Menor número = mayor prioridad
                    </Form.Text>
                </Form.Group>

                <Form.Group>
                    <Form.Label>Notas</Form.Label>
                    <Form.Control 
                        as="textarea" 
                        rows={3}
                        name="notes" 
                        value={carrierZipPrefix.notes} 
                        onChange={handleChange} 
                        data-testid="zip-prefix-notes-input"
                    />
                </Form.Group>

                <Form.Group>
                    <Form.Check 
                        type="checkbox"
                        name="disabled"
                        label="Deshabilitado"
                        checked={carrierZipPrefix.disabled}
                        onChange={handleChange}
                        data-testid="zip-prefix-disabled-checkbox"
                    />
                </Form.Group>

                <div className="d-flex justify-content-between w-100 mt-4">
                    <Button variant="primary" type="submit" data-testid="zip-prefix-submit-button">
                        {isEditing ? 'Guardar Cambios' : 'Crear Prefijo'}
                    </Button>
                    <Button variant="secondary" onClick={handleCancel} data-testid="zip-prefix-cancel-button">
                        Cancelar
                    </Button>
                </div>
            </Form>

            <EditCancelModal
                show={showCancelModal}
                onConfirm={() => navigate('/carrier-zip-prefixes')}
                onCancel={() => setShowCancelModal(false)}
            />
        </div>
    );
};

export default CarrierZipPrefixForm;